/**
 * components/rightbar/progressDomain.ts —— 「任务进展」页（kind=`progress`）**数据面**：
 * 会话事件窗口 ⇒ 节点域行（逐字承 102 两件，禁凭转述）。
 *
 * 真源（直接读原件）：
 *   · `102:frontend_src/src/components/rightbar/ProgressTabBody.vue:57-88` —— `eventOf`（三形制入窗判法）
 *     ＋ `items` computed（认领集过滤 ＋ 行形制 `{key,node,label,atText,action,errorCode,detail,sortAt}`
 *       ＋ 升序/无时刻**保序排末**的 comparator）；
 *   · `102:frontend_src/src/assembly/definitions/node-delivery.js` —— `NODE_DELIVERY_EVENT_TYPES`(:83)、
 *     `strOf`(:172)、`field`(:178)、`NON_NODE_KIND_CODES`(:176)、`nodeNameOf`(:180)、`atOf`(:227)、
 *     `plainTextMessageOf`(:241)、`momentOf`(:271)。
 *
 * 纪律（逐条）：
 *   · 事件名表**逐字抄全量**（含 ★20260925 追加三名 ＋ 过渡名 `push_node_advance`）。表头注释里的「节点域 7 名」
 *     是 20260925 之前的核心 7 名；**不得**据此删名——删名会让 `cancelled`／`chain_report`／`node_advance`
 *     在页面上凭空消失（定义门已认领，页面却不显示＝半张脸）；
 *   · `momentOf` 只投影进度行**消费到的键**（`type`/`at`/`action`/`errorCode`/`detail`）：`causes`（逐项根因）
 *     归对话流卡片（`ChainRow`），进度行不渲染 ⇒ 不带无用键（键面窄，不假装是定义层全集）；
 *   · 时刻文案 `label` 复用**同源**定义层（`renderer/nodes/NodeDeliveryRow::momentLabelOf`，禁各写一份表）；
 *   · 零 JSON 解析：`message` 的结构化键由适配层还原，本件只读放行键（与源件同款）；
 *   · 零本地时钟：时刻只取事件 `time`／`ts` 原值；无来源 ⇒ `null`（A7 不虚构时刻）。
 *
 * 归位：**纯函数数据面**（零 DOM／零响应式／零请求／零订阅）——宿主取窗口后调用，`ProgressTabBody` 仍只吃行。
 */
import { formatSessionTime, normalizeIso, parseSessionTime, type ProgressRow } from "./ProgressTabBody";
import { momentLabelOf } from "../../renderer/nodes/NodeDeliveryRow";

/** 节点域认领事件名（逐字抄 102 `node-delivery.js:83-94`；禁自造／禁改名／禁删名） */
export const NODE_DELIVERY_EVENT_TYPES: ReadonlyArray<string> = Object.freeze([
  "running", "done", "failed", "superseded", "advance", "chain_interrupted", "chain_paused",
  // ★20260925 ③-A（大王拍 A）：规范表 `EVENT_SURFACE_PLACEMENT` 定为 `card` 的三名 ⇒ 本行认领
  "cancelled", "chain_report", "node_advance",
  // ★20260925 ④：过渡名统一（SSE 侧 `push_node_advance` ↔ task_events 侧 `node_advance`，两名同认领）
  "push_node_advance",
]);

/** 域／族码（**不是节点名**；单一来源＝后端契约 `common_contract.event_kinds.values`，102 `NON_NODE_KIND_CODES:176` 逐字） */
export const NON_NODE_KIND_CODES: ReadonlySet<string> = new Set([
  "chain", "artifact", "todo", "approval", "question", "orch_step",
]);

/** 时刻条目（进度行消费键；未给的键**不入对象**，不以 `null` 冒充「已给值」——承 `momentOf`） */
export interface ProgressMoment {
  type: string | null;
  at: string | number | Date | null;
  action?: string;
  errorCode?: string;
  detail?: string;
}

/** 字符串归一（空串 ⇒ null：**不给值不当给值用**；102 `strOf` 逐字） */
function strOf(value: unknown): string | null {
  if (typeof value === "string") return value === "" ? null : value;
  if (value === null || value === undefined) return null;
  return String(value);
}

/** 载荷双认读取（先 `event.data.<name>`、再顶层 `<name>`）——本层唯一读取路径（102 `field` 逐字） */
function field(event: unknown, name: string): unknown {
  const ev = event as Record<string, unknown> | null;
  const data = ev && typeof ev.data === "object" && ev.data !== null ? (ev.data as Record<string, unknown>) : {};
  if (data[name] !== undefined && data[name] !== null) return data[name];
  return ev ? ev[name] : undefined;
}

/** 时刻（原值；`time` 优先、退回 `ts`；皆缺 ⇒ null——**禁本地时钟**；102 `atOf:227` 逐字） */
function atOf(event: unknown): string | number | Date | null {
  const t = field(event, "time");
  if (t !== null && t !== undefined) return t as string | number | Date;
  const ts = field(event, "ts");
  return ts === undefined || ts === null ? null : (ts as string | number | Date);
}

/**
 * **纯文本** `message`（★F3）：结构化 JSON 的键已由适配层还原，原文 JSON 不宜直呈人面
 * ⇒ 只放行非 JSON 行（判定用首字符 `{`／`[`——本层**不解析**）。102 `plainTextMessageOf:241` 逐字。
 */
function plainTextMessageOf(event: unknown): string | null {
  const m = strOf(field(event, "message"));
  if (m === null) return null;
  const t = m.trim();
  if (t.startsWith("{") || t.startsWith("[")) return null;
  return m;
}

/** 节点名（三档取值序；皆缺 ⇒ null）：① `kind`（**排除域／族码**）② `to_node` ③ `block_id`（102 `nodeNameOf:180` 逐字） */
export function nodeNameOf(event: unknown): string | null {
  const kind = strOf(field(event, "kind"));
  const kindAsNode = kind !== null && !NON_NODE_KIND_CODES.has(kind) ? kind : null;
  return kindAsNode ?? strOf(field(event, "to_node")) ?? strOf(field(event, "block_id"));
}

/** 窗口条目 → 事件（节点域事件既可经 `{type:'event', event}` 帧入窗，也可裸事件入窗；102 `eventOf` 逐字） */
export function eventOf(entry: unknown): Record<string, unknown> | null {
  if (!entry || typeof entry !== "object") return null;
  const e = entry as Record<string, unknown>;
  return e.event && typeof e.event === "object" ? (e.event as Record<string, unknown>) : e;
}

/** 时刻条目（102 `momentOf:271` 的进度面**子集**：`causes` 归对话流卡片，见文件头纪律） */
export function momentOf(event: unknown): ProgressMoment {
  const moment: ProgressMoment = { type: strOf(field(event, "type")), at: atOf(event) };
  const action = strOf(field(event, "action"));
  if (action !== null) moment.action = action;
  const errorCode = strOf(field(event, "error_code"));
  if (errorCode !== null) moment.errorCode = errorCode;
  // ★F3 取值序：`reason`（断点族，适配层已还原）⇒ 非 JSON 的 `message` 原文；两者皆缺 ⇒ 不入键
  const detail = strOf(field(event, "reason")) ?? plainTextMessageOf(event);
  if (detail !== null) moment.detail = detail;
  return moment;
}

/**
 * 窗口条目序列 ⇒ 节点域**进度行**（升序；无可用时刻者**保序排末**——不虚构时刻）。
 * 逐字承 102 `ProgressTabBody.vue` 的 `items` computed（含 key 形制与 comparator）。
 */
export function projectProgressRows(entries: ReadonlyArray<unknown> | null | undefined): ProgressRow[] {
  const list = Array.isArray(entries) ? entries : [];
  const rows: ProgressRow[] = [];
  for (const entry of list) {
    const ev = eventOf(entry);
    if (!ev) continue;
    const type = strOf(field(ev, "type"));
    if (type === null || !NODE_DELIVERY_EVENT_TYPES.includes(type)) continue;
    const moment = momentOf(ev);
    rows.push({
      key: `${rows.length}:${type}`,
      node: nodeNameOf(ev) ?? "",
      // 时刻文案复用同源定义层（`renderer/nodes/NodeDeliveryRow::momentLabelOf`）——
      // 该单源只读 `type`/`action`：`at` 非本节所需 ⇒ 不传（避免 Date 形制跨层）
      label: momentLabelOf({ type: moment.type ?? undefined, action: moment.action }),
      atText: formatSessionTime(normalizeIso(moment.at)),
      action: moment.action ?? "",
      errorCode: moment.errorCode ?? "",
      detail: moment.detail ?? "",
      sortAt: parseSessionTime(moment.at),
    });
  }
  return rows
    .map((row, index) => [row, index] as const)
    .sort((a, b) => {
      const ta = Number.isNaN(a[0].sortAt ?? NaN) ? Infinity : (a[0].sortAt as number);
      const tb = Number.isNaN(b[0].sortAt ?? NaN) ? Infinity : (b[0].sortAt as number);
      return ta - tb || a[1] - b[1];
    })
    .map(([row]) => row);
}
